import { getEffectiveCalculationType } from './okrCalculationType';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { previousQuarterBaseline } from './okrCycleHelpers';

const SCORING_METHOD_OPTIONS = [
    { label: 'Mayor es mejor', value: 'higher_is_better' },
    { label: 'Menor es mejor', value: 'lower_is_better' },
    { label: 'Sí / No', value: 'yes_no' },
    { label: 'Porcentaje', value: 'percentage' },
    { label: 'Hito / avance', value: 'milestone' },
    { label: 'Manual / override', value: 'manual_override' },
    { label: 'Máximo permitido', value: 'max_allowed' },
    { label: 'Compuesto por metas', value: 'composite_threshold' },
    { label: 'Compuesto', value: 'composite' }
];

const CALCULATION_TYPE_OPTIONS = [
    { label: 'Valor numérico', value: 'manual_value' },
    { label: 'Calcular varianza Forecast vs Real', value: 'variance_forecast_vs_real' },
    { label: 'Acción completada', value: 'action_completed' },
    { label: 'Meta con puntos parciales', value: 'threshold_range' },
    { label: 'Máximo permitido', value: 'max_allowed' },
    { label: 'Compuesto por metas', value: 'composite_threshold' },
    { label: 'Compuesto', value: 'composite' }
];

const UNIT_OPTIONS = ['USD', '%', 'acción', 'unidades', 'horas', 'días', 'reclamos', 'reuniones', 'tareas', 'reportes', 'procesos', 'máquinas', 'quejas', 'errores', 'clientes'].map((unit) => ({
    label: unit,
    value: unit
}));

const DATA_SOURCE_OPTIONS = [
    { label: 'Manual', value: 'manual' },
    { label: 'QuickBooks', value: 'quickbooks' },
    { label: 'Parlevel', value: 'parlevel' },
    { label: 'Syncro Operaciones', value: 'syncro_operations' },
    { label: 'Compuesta', value: 'composite' }
];

const QUICKBOOKS_BUSINESS_UNIT_OPTIONS = [
    { label: 'Empaque', value: 'empaque' },
    { label: 'Foods', value: 'foods' },
    { label: 'Coffee Vending', value: 'coffee_vending' }
];

const QUICKBOOKS_TEAM_OPTIONS = [
    { label: 'Comercial', value: 'comercial' },
    { label: 'Coffee & Vending', value: 'coffee_vending' },
    { label: 'Gerencia', value: 'gerencia' },
    { label: 'Finanzas', value: 'finanzas' }
];

const QUICKBOOKS_METRIC_OPTIONS = [
    { label: 'Ventas Coffee & Vending (Máquinas + Café + Contratos)', value: 'coffeeVendingWeeklyRevenue' },
    { label: 'Ventas netas', value: 'netSales' },
    { label: 'Facturas', value: 'invoicesAmount' },
    { label: 'Notas de crédito', value: 'creditNotesAmount' },
    { label: 'Cobrado', value: 'collected' },
    { label: '% Cobrado', value: 'collectionPercentage' },
    { label: 'Cumplimiento cuota', value: 'quotaAchievementPercentage' },
    { label: 'Cierre proyectado', value: 'projectedClosing' },
    { label: 'Retención de cuentas clave', value: 'keyAccountRetentionRate' },
    { label: 'Clientes con caída de recompra ≥20%', value: 'keyCustomerRepurchaseDropCount' }
];

const QUICKBOOKS_CUSTOMER_ACQUISITION_METRIC_OPTIONS = [
    { label: 'Nuevos clientes calificados por semana', value: 'newCustomerAccountQualification' }
];

const QUICKBOOKS_CUSTOMER_DIVERSIFICATION_METRIC_OPTIONS = [
    { label: 'Ventas netas fuera del Top 10', value: 'outsideTop10NetSales' }
];

const QUICKBOOKS_METRIC_CATEGORY_OPTIONS = [
    { label: 'Comercial / Ventas', value: 'commercial' },
    { label: 'Adquisición de clientes', value: 'customer_acquisition' },
    { label: 'Diversificación de clientes', value: 'customer_diversification' },
    { label: 'Ventas Coffee & Vending semanales', value: 'commercial_sales' },
    { label: 'Retención clientes', value: 'customer_retention' },
    { label: 'Reporte financiero', value: 'financial_report' }
];

const QUICKBOOKS_FINANCIAL_METRIC_OPTIONS = [
    { label: '% margen bruto', value: 'grossMarginPercentage' },
    { label: '% CxC vencida', value: 'overdueAccountsReceivablePercentage' },
    { label: '% CxP vencida', value: 'overdueAccountsPayablePercentage' },
    { label: 'CxC vencida', value: 'overdueAccountsReceivableAmount', disabled: true },
    { label: 'CxC total', value: 'totalAccountsReceivableAmount', disabled: true },
    { label: 'CxP vencida', value: 'overdueAccountsPayableAmount', disabled: true },
    { label: 'CxP total', value: 'totalAccountsPayableAmount', disabled: true }
];

const QUICKBOOKS_SCOPE_OPTIONS = [
    { label: 'Vendedor individual', value: 'individual_vendor' },
    { label: 'Vendedor individual CxC', value: 'individual_salesperson_accounts_receivable' },
    { label: 'Total del equipo', value: 'team_total' }
];

const THRESHOLD_OPERATOR_OPTIONS = ['>=', '<=', '>', '<', '='].map((operator) => ({ label: operator, value: operator }));

const PARLEVEL_METRIC_CATEGORY_OPTIONS = [
    { label: 'Ventas / Rutas', value: 'route_sales' },
    { label: 'Rendimiento de rutas', value: 'route_performance' },
    { label: 'Operaciones / Rutas', value: 'route_operations' },
    { label: 'Operaciones / Fallas', value: 'operations' }
];

const PARLEVEL_METRIC_OPTIONS = {
    route_sales: [
        { label: 'Ventas en rutas', value: 'routeSalesAmount' }
    ],
    route_performance: [
        { label: 'Rutas con caída de ventas vs semana previa', value: 'routeSalesDropCount' }
    ],
    operations: [
        { label: 'Máquinas con falla recurrente', value: 'recurringMachineFailureCount' }
    ],
    route_operations: [
        { label: 'Cumplimiento de rutas', value: 'routeCompliancePercentage' },
        { label: 'Cumplimiento de ruta + Nivel par', value: 'routeComplianceAndParLevel' }
    ]
};

const SYNCRO_OPERATIONS_METRIC_OPTIONS = [
    { label: 'Máquinas habilitadas para instalación', value: 'machinesReadyForInstallationCount' },
    { label: 'Máquinas de Café y Snacks en inventario', value: 'machinesInStockForInstallationCount' },
    { label: 'Casos técnicos/reclamos abiertos', value: 'openTechnicalCaseCount' },
    { label: 'Tiempo promedio de reparación', value: 'averageRepairTimeHours' },
    { label: 'Tiempo promedio de respuesta inicial por técnico', value: 'technicianAverageResponseTimeHours' },
    { label: '% de máquinas recurrentes por técnico', value: 'technicianRecurringMachineRate' },
    { label: 'Cumplimiento de preventivos', value: 'preventiveCompletionCount' },
    { label: 'Preventivos de campo por ruta', value: 'preventiveCompletionPerRoute' },
    { label: 'Tickets creados por cada 10 máquinas', value: 'ticketsPer10Machines' }
];

const MARGARITA_COMPOSITE_CONFIG = {
    components: [
        {
            key: 'attentionCoordination',
            label: 'Coordinación de atención',
            sourceType: 'parlevel',
            metricKey: 'routeComplianceAndParLevel',
            scoringMethod: 'composite_threshold',
            weight: 0.3333,
            targets: {
                routeCompliancePercentage: 98,
                routeCompliancePartialMin: 95,
                avgParLevelPercentage: 85
            }
        },
        {
            key: 'recurrentFailureMachines',
            label: 'Máquinas con falla recurrente',
            sourceType: 'parlevel',
            metricKey: 'recurringMachineFailureCount',
            target: 0,
            scoringMethod: 'max_allowed',
            weight: 0.3333,
            filters: {
                failureThreshold: 3
            }
        },
        {
            key: 'preventiveCompletion',
            label: 'Cumplimiento de preventivos',
            sourceType: 'syncro_operations',
            metricKey: 'preventiveCompletionCount',
            target: 27,
            scoringMethod: 'higher_is_better',
            weight: 0.3333,
            filters: {
                taskTypes: ['Fumigation', 'General Cleaning', 'Fumigation and General Cleaning'],
                routeCount: 9,
                expectedPerRoute: 3
            }
        }
    ]
};

const MAURICIO_COMPOSITE_CONFIG = {
    components: [
        {
            key: 'preventiveCompletion',
            label: 'Preventivos completados en rutas',
            sourceType: 'syncro_operations',
            metricKey: 'preventiveCompletionCount',
            target: 30,
            scoringMethod: 'higher_is_better',
            weight: 0.5,
            filters: {
                inRoutesOnly: true
            }
        },
        {
            key: 'openCasesWeeklyClose',
            label: 'Casos abiertos al cierre semanal',
            sourceType: 'syncro_operations',
            metricKey: 'openTechnicalCaseCount',
            target: 75,
            scoringMethod: 'lower_is_better',
            weight: 0.5,
            dateRangeMode: 'okr_week_end'
        }
    ]
};

const COMPOSITE_METRIC_OPTIONS = [
    { label: 'Fallas recurrentes + Preventivos + Coordinación', value: 'margaritaFailurePreventionAttentionComposite' },
    { label: 'Preventivos completados + Casos abiertos al cierre', value: 'mauricioPreventiveOpenCasesComposite' }
];

const getCompositeConfigForMetric = (metricKey) => (
    metricKey === 'mauricioPreventiveOpenCasesComposite' ? MAURICIO_COMPOSITE_CONFIG : MARGARITA_COMPOSITE_CONFIG
);

const getId = (value) => value?._id || value?.id || value || '';

const DEFAULT_COMPOSITE_CONFIG = {
    components: [
        {
            key: 'routeCompliancePercentage',
            label: 'Cumplimiento de ruta',
            target: 98,
            partialMin: 95,
            weight: 0.5,
            direction: 'higher_is_better'
        },
        {
            key: 'avgParLevelPercentage',
            label: 'Nivel par promedio',
            target: 85,
            weight: 0.5,
            direction: 'higher_is_better'
        }
    ]
};

const EMPTY_AUTOMATION_CONFIG = {
    sourceType: 'manual',
    metricCategory: 'commercial',
    metricKey: 'netSales',
    reportKey: '',
    aggregationScope: 'individual_vendor',
    businessUnit: 'empaque',
    businessUnits: ['empaque'],
    team: 'comercial',
    vendorScope: 'individual_vendor',
    vendorId: '',
    vendorName: '',
    syncroUserId: '',
    dateRangeMode: 'okr_week',
    division: '',
    classId: '',
    locationId: '',
    thresholdMetricKey: '',
    thresholdOperator: '>=',
    thresholdValue: null,
    thresholdConfig: {
        targetValue: 98,
        partialMinValue: 95,
        direction: 'higher_is_better'
    },
    thresholds: {
        fullPointsAtOrBelow: 1,
        halfPointsAt: 2,
        zeroPointsAtOrAbove: 3
    },
    compositeConfig: DEFAULT_COMPOSITE_CONFIG,
    filters: {},
    isEnabled: false
};

const EMPTY_FORM = {
    ownerId: '',
    pointOwnerType: 'user',
    pointOwnerUserId: '',
    pointOwnerName: '',
    updateResponsibleUserId: '',
    title: '',
    description: '',
    weight: null,
    weeklyTarget: null,
    unit: '',
    calculationType: 'manual_value',
    scoringMethod: 'higher_is_better',
    allowOverTarget: true,
    maxMultiplier: null,
    automationConfig: EMPTY_AUTOMATION_CONFIG
};

const buildAutomationConfig = (automationConfig = {}) => ({
    ...EMPTY_AUTOMATION_CONFIG,
    ...automationConfig,
    sourceType: automationConfig?.sourceType || 'manual',
    metricCategory: automationConfig?.metricCategory || (automationConfig?.sourceType === 'syncro_operations' ? 'technical_operations' : automationConfig?.sourceType === 'composite' ? 'operations_quality' : 'commercial'),
    metricKey: automationConfig?.metricKey || (automationConfig?.sourceType === 'syncro_operations' ? 'machinesReadyForInstallationCount' : automationConfig?.sourceType === 'composite' ? 'margaritaFailurePreventionAttentionComposite' : 'netSales'),
    reportKey: automationConfig?.reportKey || '',
    aggregationScope: automationConfig?.aggregationScope || (automationConfig?.sourceType === 'syncro_operations' ? 'technical_team' : 'individual_vendor'),
    businessUnit: automationConfig?.businessUnit || 'empaque',
    businessUnits: Array.isArray(automationConfig?.businessUnits) && automationConfig.businessUnits.length
        ? automationConfig.businessUnits
        : [automationConfig?.businessUnit || 'empaque'],
    team: automationConfig?.team || 'comercial',
    vendorScope: automationConfig?.vendorScope || (automationConfig?.aggregationScope === 'team_total' ? 'all_team' : 'individual_vendor'),
    vendorName: automationConfig?.vendorName || '',
    dateRangeMode: automationConfig?.dateRangeMode || 'okr_week',
    division: automationConfig?.division || '',
    classId: automationConfig?.classId || '',
    locationId: automationConfig?.locationId || '',
    thresholdMetricKey: automationConfig?.thresholdMetricKey || '',
    thresholdOperator: automationConfig?.thresholdOperator || '>=',
    thresholdValue: automationConfig?.thresholdValue ?? null,
    thresholdConfig: automationConfig?.thresholdConfig || {
        targetValue: 98,
        partialMinValue: 95,
        direction: 'higher_is_better'
    },
    thresholds: automationConfig?.thresholds || {
        fullPointsAtOrBelow: 1,
        halfPointsAt: 2,
        zeroPointsAtOrAbove: 3
    },
    compositeConfig: automationConfig?.compositeConfig || (automationConfig?.sourceType === 'composite' ? getCompositeConfigForMetric(automationConfig?.metricKey) : DEFAULT_COMPOSITE_CONFIG),
    filters: automationConfig?.filters || {},
    isEnabled: automationConfig?.sourceType && automationConfig.sourceType !== 'manual' ? automationConfig.isEnabled !== false : false
});

const buildInitialForm = (keyResult, initialOwnerId = '') => {
    if (!keyResult) {
        return {
            ...EMPTY_FORM,
            ownerId: initialOwnerId || '',
            pointOwnerUserId: initialOwnerId || '',
            updateResponsibleUserId: initialOwnerId || '',
            automationConfig: { ...EMPTY_AUTOMATION_CONFIG }
        };
    }

    const ownerId =
        keyResult.ownerId?._id ||
        keyResult.ownerId ||
        keyResult.pointOwnerUserId?._id ||
        keyResult.pointOwnerUserId ||
        keyResult.updateResponsibleUserId?._id ||
        keyResult.updateResponsibleUserId ||
        '';

    return {
        ownerId,
        pointOwnerType: 'user',
        pointOwnerUserId: ownerId,
        pointOwnerName: '',
        updateResponsibleUserId: ownerId,
        title: keyResult.title || '',
        description: keyResult.description || '',
        weight: keyResult.weight ?? null,
        weeklyTarget: keyResult.weeklyTarget ?? null,
        unit: keyResult.unit || '',
        calculationType: keyResult.calculationType || 'manual_value',
        scoringMethod: keyResult.scoringMethod || 'higher_is_better',
        allowOverTarget: keyResult.allowOverTarget !== false,
        maxMultiplier: keyResult.maxMultiplier ?? null,
        automationConfig: buildAutomationConfig(keyResult.automationConfig)
    };
};

const needsWeeklyTarget = (scoringMethod) => ['higher_is_better', 'lower_is_better', 'threshold_range'].includes(scoringMethod);

const roundToTwo = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return Math.round(number * 100) / 100;
};

const getCalculationPreview = (scoringMethod) => {
    const previews = {
        higher_is_better: 'Ejemplo: si el real supera la meta, los puntos aumentan proporcionalmente.',
        lower_is_better: 'Ejemplo: si el real queda por debajo de la meta, los puntos aumentan proporcionalmente.',
        yes_no: 'Si se cumple, gana los puntos semanales completos. Si no, gana 0.',
        percentage: 'Los puntos se calculan según el porcentaje completado.',
        milestone: 'Los puntos se calculan según el avance reportado del hito.',
        manual_override: 'Un admin podrá definir los puntos manualmente; el cálculo base queda como referencia.'
    };

    return previews[scoringMethod] || '';
};

const OkrKeyResultDialog = ({ visible, keyResult, team, objective, cycle, users, saving, onHide, onSubmit }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const isEditMode = Boolean(keyResult?._id);

    const title = useMemo(() => (isEditMode ? 'Editar resultado clave medible' : 'Agregar resultado clave medible'), [isEditMode]);

    const objectiveResponsibleIds = useMemo(
        () => (Array.isArray(objective?.responsibleUserIds) ? objective.responsibleUserIds.map(getId).filter(Boolean) : []),
        [objective]
    );

    const inferredOwnerId = !isEditMode && objectiveResponsibleIds.length === 1 ? objectiveResponsibleIds[0] : '';
    const hasSingleInferredOwner = Boolean(inferredOwnerId);
    const hasMultipleObjectiveResponsibles = !isEditMode && objectiveResponsibleIds.length > 1;

    useEffect(() => {
        if (visible) {
            setForm(buildInitialForm(keyResult, inferredOwnerId));
            setErrors({});
        }
    }, [inferredOwnerId, keyResult, visible]);

    const allUserOptions = useMemo(
        () =>
            (users || []).map((user) => ({
                label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Usuario sin nombre',
                value: user._id
            })),
        [users]
    );

    const ownerUserOptions = useMemo(
        () => {
            const allowedIds = hasMultipleObjectiveResponsibles ? new Set(objectiveResponsibleIds) : null;
            return allUserOptions.filter((option) => !allowedIds || allowedIds.has(getId(option.value)));
        },
        [allUserOptions, hasMultipleObjectiveResponsibles, objectiveResponsibleIds]
    );

    const numberOfWeeks = Number(cycle?.numberOfWeeks || 0);
    const weeklyBasePoints = form.weight && numberOfWeeks > 0 ? roundToTwo(Number(form.weight) / numberOfWeeks) : 0;
    const calculationPreview = getCalculationPreview(form.scoringMethod);

    const updateField = (field, value) => {
        setForm((current) => {
            const next = { ...current, [field]: value };

            if (field === 'ownerId') {
                next.pointOwnerType = 'user';
                next.pointOwnerUserId = value || '';
                next.pointOwnerName = '';
                next.updateResponsibleUserId = value || '';
            }

            if (field === 'scoringMethod' && ['percentage', 'milestone'].includes(value) && !current.unit) {
                next.unit = '%';
            }

            if (field === 'calculationType' && value === 'variance_forecast_vs_real') {
                next.unit = '%';
                next.scoringMethod = 'lower_is_better';
            }

            if (field === 'calculationType' && value === 'action_completed') {
                next.unit = 'acción';
                next.weeklyTarget = 1;
                next.scoringMethod = 'yes_no';
                next.allowOverTarget = false;
            }

            if (field === 'calculationType' && value === 'max_allowed') {
                next.scoringMethod = 'max_allowed';
                next.allowOverTarget = false;
            }

            return next;
        });
        setErrors((current) => ({ ...current, [field]: null }));
    };

    const updateAutomationField = (field, value) => {
        setForm((current) => {
            if (field === 'sourceType' && value === 'manual') {
                return {
                    ...current,
                    automationConfig: { ...EMPTY_AUTOMATION_CONFIG }
                };
            }

            return {
                ...current,
                automationConfig: {
                    ...current.automationConfig,
                    [field]: value,
                    ...(field === 'sourceType' ? { isEnabled: value !== 'manual' } : {}),
                    ...(field === 'sourceType' && value === 'quickbooks'
                        ? { metricCategory: current.automationConfig.metricCategory || 'commercial', aggregationScope: current.automationConfig.aggregationScope || 'individual_vendor' }
                        : {}),
                    ...(field === 'sourceType' && value === 'parlevel'
                        ? {
                            metricCategory: ['operations', 'route_operations', 'route_performance'].includes(current.automationConfig.metricCategory) ? current.automationConfig.metricCategory : 'route_sales',
                            metricKey: current.automationConfig.metricKey === 'recurringMachineFailureCount'
                                ? 'recurringMachineFailureCount'
                                : current.automationConfig.metricKey === 'routeSalesDropCount'
                                    ? 'routeSalesDropCount'
                                : current.automationConfig.metricKey === 'routeComplianceAndParLevel'
                                    ? 'routeComplianceAndParLevel'
                                : current.automationConfig.metricKey === 'routeCompliancePercentage'
                                    ? 'routeCompliancePercentage'
                                    : 'routeSalesAmount',
                            aggregationScope: ['routeCompliancePercentage', 'routeComplianceAndParLevel'].includes(current.automationConfig.metricKey) ? 'all_routes' : 'all_machines',
                            vendorScope: null,
                            vendorName: '',
                            vendorId: '',
                            dateRangeMode: 'okr_week',
                            filters: {
                                machineScope: 'all',
                                routeScope: 'all',
                                failureThreshold: current.automationConfig.filters?.failureThreshold || 2
                            }
                        }
                        : {}),
                    ...(field === 'sourceType' && value === 'syncro_operations'
                        ? {
                            metricCategory: 'technical_operations',
                            metricKey: 'machinesReadyForInstallationCount',
                            aggregationScope: 'technical_team',
                            vendorScope: null,
                            vendorName: '',
                            vendorId: '',
                            dateRangeMode: 'okr_week',
                            filters: {}
                        }
                        : {}),
                    ...(field === 'sourceType' && value === 'composite'
                        ? {
                            metricCategory: current.automationConfig.metricKey === 'mauricioPreventiveOpenCasesComposite' ? 'preventive_operations' : 'operations_quality',
                            metricKey: current.automationConfig.metricKey === 'mauricioPreventiveOpenCasesComposite'
                                ? 'mauricioPreventiveOpenCasesComposite'
                                : 'margaritaFailurePreventionAttentionComposite',
                            aggregationScope: 'all_machines',
                            vendorScope: null,
                            vendorName: '',
                            vendorId: '',
                            dateRangeMode: 'okr_week',
                            compositeConfig: getCompositeConfigForMetric(current.automationConfig.metricKey),
                            filters: {}
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'route_operations'
                        ? {
                            metricKey: 'routeComplianceAndParLevel',
                            aggregationScope: 'all_routes',
                            dateRangeMode: 'okr_week',
                            thresholdConfig: {
                                targetValue: current.automationConfig.thresholdConfig?.targetValue || 98,
                                partialMinValue: current.automationConfig.thresholdConfig?.partialMinValue || 95,
                                direction: 'higher_is_better'
                            },
                            compositeConfig: current.automationConfig.compositeConfig || DEFAULT_COMPOSITE_CONFIG,
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                routeScope: 'all'
                            }
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'financial_report'
                        ? {
                            metricKey: 'overdueAccountsPayablePercentage',
                            reportKey: 'accounts_payable_aging',
                            aggregationScope: 'team_total',
                            vendorScope: 'all_team',
                            vendorName: '',
                            vendorId: '',
                            dateRangeMode: 'okr_week_end'
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'overdueAccountsReceivablePercentage'
                        ? {
                            reportKey: 'accounts_receivable_aging',
                            aggregationScope: 'individual_salesperson_accounts_receivable',
                            vendorScope: 'individual_vendor',
                            dateRangeMode: 'okr_week_end'
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'grossMarginPercentage'
                        ? { reportKey: 'profit_and_loss', aggregationScope: 'team_total', vendorScope: 'all_team', vendorName: '', vendorId: '', dateRangeMode: 'okr_week' }
                        : {}),
                    ...(field === 'metricKey' && value === 'overdueAccountsPayablePercentage'
                        ? {
                            reportKey: 'accounts_payable_aging',
                            aggregationScope: 'team_total',
                            vendorScope: 'all_team',
                            vendorName: '',
                            vendorId: '',
                            dateRangeMode: 'okr_week_end'
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'coffeeVendingWeeklyRevenue'
                        ? {
                            metricCategory: 'commercial_sales',
                            reportKey: 'coffee_vending_weekly_revenue',
                            aggregationScope: 'team_total',
                            businessUnit: 'coffee_vending',
                            businessUnits: ['coffee_vending'],
                            team: 'coffee_vending',
                            vendorScope: 'all_team',
                            vendorName: '',
                            vendorId: '',
                            dateRangeMode: 'okr_week',
                            filters: {
                                accountingMethod: 'Accrual',
                                machineSalesSource: 'parlevel_route_sales',
                                coffeeAccountId: '167',
                                contractsAccountId: '1150040007'
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'keyCustomerRepurchaseDropCount'
                        ? {
                            metricCategory: 'commercial',
                            aggregationScope: current.automationConfig.aggregationScope === 'team_total' ? 'team_total' : 'individual_vendor',
                            vendorScope: current.automationConfig.aggregationScope === 'team_total' ? 'all_team' : 'individual_vendor',
                            reportKey: '',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                dropThresholdPercentage: current.automationConfig.filters?.dropThresholdPercentage ?? 20,
                                comparisonBasis: 'trailing_3_month_average'
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'keyAccountRetentionRate'
                        ? {
                            metricCategory: 'customer_retention',
                            aggregationScope: 'individual_vendor',
                            vendorScope: 'individual_vendor',
                            vendorName: 'Administrativo',
                            reportKey: '',
                            dateRangeMode: 'rolling_4_weeks_as_of_okr_week_end',
                            businessUnits: ['empaque', 'foods'],
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                customerScope: 'administracion_accounts',
                                quickbooksVendorName: 'Administrativo',
                                displayPortfolioName: 'Administracion',
                                businessUnits: ['empaque', 'foods'],
                                baselineMode: 'previous_3_month_average',
                                atRiskDropThresholdPercentage: current.automationConfig.filters?.atRiskDropThresholdPercentage ?? 20
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'newCustomerAccountQualification'
                        ? {
                            metricCategory: 'customer_acquisition',
                            aggregationScope: 'team_total',
                            businessUnit: 'empaque',
                            businessUnits: ['empaque', 'foods'],
                            team: 'comercial',
                            vendorScope: 'all_team',
                            vendorName: '',
                            vendorId: '',
                            reportKey: '',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                requiredNewCustomerCount: current.automationConfig.filters?.requiredNewCustomerCount ?? 2
                            },
                            thresholdConfig: {
                                targetValue: 2,
                                partialMinValue: 0,
                                direction: 'higher_is_better'
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'outsideTop10NetSales'
                        ? {
                            metricCategory: 'customer_diversification',
                            aggregationScope: 'team_total',
                            businessUnit: 'empaque',
                            businessUnits: ['empaque', 'foods'],
                            team: 'comercial',
                            vendorScope: 'all_team',
                            vendorName: '',
                            vendorId: '',
                            reportKey: '',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...previousQuarterBaseline(cycle),
                                topCustomerCount: 10,
                                monthlyTargets: { empaque: 27500, foods: 14000 }
                            },
                            compositeConfig: {
                                components: [
                                    { key: 'empaque', label: 'Empaques', target: 6346.15, partialMin: 0, weight: 0.5, direction: 'higher_is_better' },
                                    { key: 'foods', label: 'Foods', target: 3230.77, partialMin: 0, weight: 0.5, direction: 'higher_is_better' }
                                ]
                            }
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'commercial'
                        ? {
                            metricKey: QUICKBOOKS_METRIC_OPTIONS.some((option) => option.value === current.automationConfig.metricKey) ? current.automationConfig.metricKey : 'netSales',
                            reportKey: '',
                            dateRangeMode: 'okr_week'
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'commercial_sales'
                        ? {
                            metricKey: 'coffeeVendingWeeklyRevenue',
                            reportKey: 'coffee_vending_weekly_revenue',
                            aggregationScope: 'team_total',
                            businessUnit: 'coffee_vending',
                            businessUnits: ['coffee_vending'],
                            team: 'coffee_vending',
                            vendorScope: 'all_team',
                            vendorName: '',
                            vendorId: '',
                            dateRangeMode: 'okr_week',
                            filters: {
                                accountingMethod: 'Accrual',
                                machineSalesSource: 'parlevel_route_sales',
                                coffeeAccountId: '167',
                                contractsAccountId: '1150040007'
                            }
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'customer_acquisition'
                        ? {
                            metricKey: 'newCustomerAccountQualification',
                            aggregationScope: 'team_total',
                            businessUnit: 'empaque',
                            businessUnits: ['empaque', 'foods'],
                            team: 'comercial',
                            vendorScope: 'all_team',
                            vendorName: '',
                            vendorId: '',
                            reportKey: '',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                requiredNewCustomerCount: current.automationConfig.filters?.requiredNewCustomerCount ?? 2
                            },
                            thresholdConfig: {
                                targetValue: 2,
                                partialMinValue: 0,
                                direction: 'higher_is_better'
                            }
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'customer_diversification'
                        ? {
                            metricKey: 'outsideTop10NetSales',
                            aggregationScope: 'team_total',
                            businessUnit: 'empaque',
                            businessUnits: ['empaque', 'foods'],
                            team: 'comercial',
                            vendorScope: 'all_team',
                            vendorName: '',
                            vendorId: '',
                            reportKey: '',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...previousQuarterBaseline(cycle),
                                topCustomerCount: 10,
                                monthlyTargets: { empaque: 27500, foods: 14000 }
                            },
                            compositeConfig: {
                                components: [
                                    { key: 'empaque', label: 'Empaques', target: 6346.15, partialMin: 0, weight: 0.5, direction: 'higher_is_better' },
                                    { key: 'foods', label: 'Foods', target: 3230.77, partialMin: 0, weight: 0.5, direction: 'higher_is_better' }
                                ]
                            }
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'route_sales'
                        ? {
                            metricKey: 'routeSalesAmount',
                            aggregationScope: 'all_machines',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                machineScope: 'all',
                                routeScope: 'all'
                            }
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'route_performance'
                        ? {
                            metricKey: 'routeSalesDropCount',
                            aggregationScope: 'all_routes',
                            dateRangeMode: 'okr_week_vs_previous_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                comparison: 'previous_week',
                                dropThresholdPercentage: current.automationConfig.filters?.dropThresholdPercentage ?? 15
                            }
                        }
                        : {}),
                    ...(field === 'metricCategory' && value === 'operations'
                        ? {
                            metricKey: 'recurringMachineFailureCount',
                            aggregationScope: 'all_machines',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                machineScope: 'all',
                                failureThreshold: current.automationConfig.filters?.failureThreshold || 2
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'routeSalesAmount'
                        ? {
                            metricCategory: 'route_sales',
                            aggregationScope: 'all_machines',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                machineScope: 'all',
                                routeScope: 'all'
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'routeSalesDropCount'
                        ? {
                            metricCategory: 'route_performance',
                            aggregationScope: 'all_routes',
                            dateRangeMode: 'okr_week_vs_previous_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                comparison: 'previous_week',
                                dropThresholdPercentage: current.automationConfig.filters?.dropThresholdPercentage ?? 15
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'recurringMachineFailureCount'
                        ? {
                            metricCategory: 'operations',
                            aggregationScope: 'all_machines',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                machineScope: 'all',
                                failureThreshold: current.automationConfig.filters?.failureThreshold || 2
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'routeCompliancePercentage'
                        ? {
                            metricCategory: 'route_operations',
                            aggregationScope: 'all_routes',
                            dateRangeMode: 'okr_week',
                            thresholdConfig: {
                                targetValue: current.automationConfig.thresholdConfig?.targetValue || 98,
                                partialMinValue: current.automationConfig.thresholdConfig?.partialMinValue || 95,
                                direction: 'higher_is_better'
                            },
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                routeScope: 'all'
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'routeComplianceAndParLevel'
                        ? {
                            metricCategory: 'route_operations',
                            aggregationScope: 'all_routes',
                            dateRangeMode: 'okr_week',
                            compositeConfig: current.automationConfig.compositeConfig || DEFAULT_COMPOSITE_CONFIG,
                            filters: {
                                ...(current.automationConfig.filters || {}),
                                routeScope: 'all'
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'machinesInStockForInstallationCount'
                        ? { metricCategory: 'technical_operations', aggregationScope: 'all_machines', dateRangeMode: 'okr_week', filters: {} }
                        : {}),
                    ...(field === 'metricKey' && value === 'machinesReadyForInstallationCount'
                        ? {
                            metricCategory: 'technical_operations',
                            aggregationScope: 'technical_team',
                            dateRangeMode: 'okr_week',
                            filters: {}
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'openTechnicalCaseCount'
                        ? {
                            metricCategory: 'technical_operations',
                            aggregationScope: 'technical_team',
                            dateRangeMode: 'okr_week_end',
                            filters: {}
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'averageRepairTimeHours'
                        ? {
                            metricCategory: 'technical_operations',
                            aggregationScope: 'technical_team',
                            dateRangeMode: 'okr_week',
                            filters: {}
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'technicianAverageResponseTimeHours'
                        ? {
                            metricCategory: 'technical_operations',
                            aggregationScope: 'individual_technician',
                            dateRangeMode: 'okr_week',
                            filters: {}
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'technicianRecurringMachineRate'
                        ? {
                            metricCategory: 'technical_quality',
                            aggregationScope: 'individual_technician',
                            dateRangeMode: 'okr_week',
                            filters: { lookbackDays: 30 }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'preventiveCompletionCount'
                        ? {
                            metricCategory: 'technical_operations',
                            aggregationScope: 'technical_team',
                            dateRangeMode: 'okr_week',
                            filters: {
                                taskTypes: ['Fumigation', 'General Cleaning', 'Fumigation and General Cleaning'],
                                routeCount: 9,
                                expectedPerRoute: 3
                            }
                        }
                        : {}),
                    ...(field === 'metricKey' && value === 'ticketsPer10Machines'
                        ? {
                            metricCategory: 'technical_quality',
                            aggregationScope: 'technical_team',
                            dateRangeMode: 'okr_week',
                            filters: {
                                ownerUserName: 'Julio Ortiz',
                                scope: 'assigned_machines'
                            },
                            thresholds: {
                                fullPointsAtOrBelow: 1,
                                halfPointsAt: 2,
                                zeroPointsAtOrAbove: 3
                            }
                        }
                        : {}),
                    ...(field === 'thresholdConfig' ? { thresholdConfig: { ...(current.automationConfig.thresholdConfig || {}), ...(value || {}) } } : {}),
                    ...(field === 'thresholds' ? { thresholds: { ...(current.automationConfig.thresholds || {}), ...(value || {}) } } : {}),
                    ...(field === 'compositeConfig' ? { compositeConfig: { ...(current.automationConfig.compositeConfig || DEFAULT_COMPOSITE_CONFIG), ...(value || {}) } } : {}),
                    ...(field === 'metricKey' && ['margaritaFailurePreventionAttentionComposite', 'mauricioPreventiveOpenCasesComposite'].includes(value)
                        ? {
                            metricCategory: value === 'mauricioPreventiveOpenCasesComposite' ? 'preventive_operations' : 'operations_quality',
                            compositeConfig: getCompositeConfigForMetric(value),
                        }
                        : {}),
                    ...(field === 'filters' ? { filters: { ...(current.automationConfig.filters || {}), ...(value || {}) } } : {}),
                    ...(field === 'aggregationScope' && value === 'team_total'
                        ? { vendorScope: 'all_team', vendorName: '', vendorId: '', businessUnits: current.automationConfig.businessUnits?.length ? current.automationConfig.businessUnits : [current.automationConfig.businessUnit || 'empaque'] }
                        : {}),
                    ...(field === 'aggregationScope' && value === 'individual_vendor'
                        ? { vendorScope: 'individual_vendor', businessUnit: current.automationConfig.businessUnit || current.automationConfig.businessUnits?.[0] || 'empaque' }
                        : {}),
                    ...(field === 'businessUnit' ? { businessUnits: [value] } : {}),
                    ...(field === 'businessUnits' ? { businessUnit: value?.[0] || current.automationConfig.businessUnit || 'empaque' } : {})
                }
            };
        });
        if (field === 'metricKey' && value === 'grossMarginPercentage') {
            setForm(current => ({ ...current, unit: '%', weeklyTarget: 30, calculationType: 'composite_threshold', scoringMethod: 'composite_threshold', allowOverTarget: false,
                automationConfig: { ...current.automationConfig, compositeConfig: {
                    components: (current.automationConfig.businessUnits?.length ? current.automationConfig.businessUnits : [current.automationConfig.businessUnit || 'empaque']).map((key, index, units) => ({
                        key, label: `Margen bruto ${key}`, target: key === 'foods' ? 35 : 30, weight: 1 / units.length, direction: 'higher_is_better'
                    }))
                } }
            }));
        }
        if (field === 'metricCategory' && value === 'financial_report') {
            setForm((current) => ({
                ...current,
                unit: '%',
                scoringMethod: 'lower_is_better'
            }));
        }
        if (
            (field === 'metricCategory' && value === 'commercial_sales') ||
            (field === 'metricKey' && value === 'coffeeVendingWeeklyRevenue')
        ) {
            setForm((current) => ({
                ...current,
                unit: 'USD',
                weeklyTarget: 53545.77,
                calculationType: 'manual_value',
                scoringMethod: 'higher_is_better',
                allowOverTarget: true
            }));
        }
        if (field === 'metricKey' && value === 'newCustomerAccountQualification') {
            setForm((current) => ({
                ...current,
                unit: 'clientes',
                weeklyTarget: 2,
                calculationType: 'threshold_range',
                scoringMethod: 'threshold_range',
                allowOverTarget: false
            }));
        }
        if ((field === 'metricCategory' && value === 'customer_diversification') || (field === 'metricKey' && value === 'outsideTop10NetSales')) {
            setForm((current) => ({
                ...current,
                unit: 'USD',
                weeklyTarget: 9576.92,
                calculationType: 'composite_threshold',
                scoringMethod: 'composite_threshold',
                allowOverTarget: false
            }));
        }
        if ((field === 'sourceType' && value === 'parlevel') || (field === 'metricCategory' && value === 'route_sales') || (field === 'metricKey' && value === 'routeSalesAmount')) {
            setForm((current) => ({
                ...current,
                unit: current.unit || 'USD',
                calculationType: current.calculationType === 'max_allowed' ? 'manual_value' : current.calculationType,
                scoringMethod: current.scoringMethod === 'max_allowed' ? 'higher_is_better' : current.scoringMethod || 'higher_is_better',
                allowOverTarget: true
            }));
        }
        if ((field === 'metricCategory' && value === 'route_performance') || (field === 'metricKey' && value === 'routeSalesDropCount')) {
            setForm((current) => ({
                ...current,
                unit: 'rutas',
                weeklyTarget: 0,
                calculationType: 'max_allowed',
                scoringMethod: 'max_allowed',
                allowOverTarget: false
            }));
        }
        if ((field === 'metricCategory' && value === 'operations') || (field === 'metricKey' && value === 'recurringMachineFailureCount')) {
            setForm((current) => ({
                ...current,
                unit: 'máquinas',
                weeklyTarget: 0,
                calculationType: 'max_allowed',
                scoringMethod: 'max_allowed',
                allowOverTarget: false
            }));
        }
        if ((field === 'metricCategory' && value === 'route_operations') || (field === 'metricKey' && value === 'routeCompliancePercentage')) {
            setForm((current) => ({
                ...current,
                unit: '%',
                weeklyTarget: value === 'routeCompliancePercentage' ? current.automationConfig.thresholdConfig?.targetValue || 98 : 100,
                calculationType: value === 'routeCompliancePercentage' ? 'threshold_range' : 'composite_threshold',
                scoringMethod: value === 'routeCompliancePercentage' ? 'threshold_range' : 'composite_threshold',
                allowOverTarget: false
            }));
        }
        if (field === 'metricKey' && value === 'routeComplianceAndParLevel') {
            setForm((current) => ({
                ...current,
                unit: '%',
                weeklyTarget: 100,
                calculationType: 'composite_threshold',
                scoringMethod: 'composite_threshold',
                allowOverTarget: false
            }));
        }
        if ((field === 'sourceType' && value === 'syncro_operations') || (field === 'metricKey' && value === 'machinesReadyForInstallationCount')) {
            setForm((current) => ({
                ...current,
                unit: 'máquinas',
                weeklyTarget: current.weeklyTarget || 3,
                calculationType: 'manual_value',
                scoringMethod: 'higher_is_better',
                allowOverTarget: false
            }));
        }
        if (field === 'metricKey' && value === 'openTechnicalCaseCount') {
            setForm((current) => ({
                ...current,
                unit: 'casos',
                weeklyTarget: 10,
                calculationType: 'manual_value',
                scoringMethod: 'lower_is_better',
                allowOverTarget: false
            }));
        }
        if (field === 'metricKey' && value === 'averageRepairTimeHours') {
            setForm((current) => ({
                ...current,
                unit: 'horas',
                weeklyTarget: 24,
                calculationType: 'manual_value',
                scoringMethod: 'lower_is_better',
                allowOverTarget: false
            }));
        }
        if (field === 'metricKey' && value === 'preventiveCompletionCount') {
            setForm((current) => ({
                ...current,
                unit: 'tareas',
                weeklyTarget: 27,
                calculationType: 'manual_value',
                scoringMethod: 'higher_is_better',
                allowOverTarget: false
            }));
        }
        if (field === 'metricKey' && value === 'ticketsPer10Machines') {
            setForm((current) => ({
                ...current,
                unit: 'tickets / 10 máquinas',
                weeklyTarget: 1,
                calculationType: 'threshold_range',
                scoringMethod: 'threshold_range',
                allowOverTarget: false
            }));
        }
        if (field === 'sourceType' && value === 'composite') {
            setForm((current) => ({
                ...current,
                unit: 'puntos compuestos',
                weeklyTarget: 100,
                calculationType: 'composite',
                scoringMethod: 'composite',
                allowOverTarget: false
            }));
        }
        if (field === 'metricKey' && value === 'mauricioPreventiveOpenCasesComposite') {
            setForm((current) => ({
                ...current,
                unit: 'puntos compuestos',
                weeklyTarget: 100,
                calculationType: 'composite',
                scoringMethod: 'composite',
                allowOverTarget: false
            }));
        }
        setErrors((current) => ({ ...current, automationConfig: null }));
    };

    const validate = () => {
        const nextErrors = {};

        if (!objective?._id) nextErrors.objectiveId = 'El objetivo es obligatorio.';
        if (!form.ownerId) nextErrors.ownerId = 'Selecciona la persona responsable.';
        if (!form.title?.trim()) nextErrors.title = 'El título del resultado clave es obligatorio.';
        if (!form.weight || Number(form.weight) <= 0) nextErrors.weight = 'El peso debe ser mayor que 0.';
        if (form.calculationType === 'max_allowed' && (form.weeklyTarget === null || form.weeklyTarget === undefined || form.weeklyTarget === '' || Number(form.weeklyTarget) < 0)) {
            nextErrors.weeklyTarget = 'La meta máxima permitida debe ser 0 o mayor.';
        }
        if (form.calculationType !== 'action_completed' && form.calculationType !== 'max_allowed' && needsWeeklyTarget(form.scoringMethod) && (!form.weeklyTarget || Number(form.weeklyTarget) <= 0)) {
            nextErrors.weeklyTarget = 'La meta es obligatoria para este método.';
        }
        if (form.maxMultiplier !== null && form.maxMultiplier !== undefined && form.maxMultiplier !== '' && Number(form.maxMultiplier) <= 0) {
            nextErrors.maxMultiplier = 'El multiplicador máximo debe ser mayor que 0.';
        }
        if (form.automationConfig?.sourceType === 'quickbooks') {
            const isKeyAccountRetention = form.automationConfig.metricKey === 'keyAccountRetentionRate';
            const isCoffeeVendingWeeklyRevenue = form.automationConfig.metricKey === 'coffeeVendingWeeklyRevenue';
            if (form.automationConfig.metricCategory === 'financial_report') {
                if (!form.automationConfig.businessUnits?.length) nextErrors.automationConfig = 'Selecciona al menos una unidad de negocio para el reporte financiero.';
                if (!['overdueAccountsPayablePercentage', 'overdueAccountsReceivablePercentage', 'grossMarginPercentage'].includes(form.automationConfig.metricKey)) nextErrors.automationConfig = 'Selecciona una métrica financiera válida.';
                if (form.automationConfig.metricKey === 'overdueAccountsReceivablePercentage' && !form.automationConfig.vendorName?.trim()) nextErrors.automationConfig = 'Escribe el vendedor QuickBooks para calcular CxC vencida.';
            } else if (form.automationConfig.aggregationScope === 'team_total') {
                if (!form.automationConfig.businessUnits?.length) nextErrors.automationConfig = 'Selecciona al menos una unidad de negocio de QuickBooks.';
            } else if (!isKeyAccountRetention && !form.automationConfig.businessUnit) {
                nextErrors.automationConfig = 'Selecciona la unidad de negocio de QuickBooks.';
            }
            if (!form.automationConfig.metricKey) nextErrors.automationConfig = 'Selecciona la métrica de QuickBooks.';
            if (isKeyAccountRetention && !form.automationConfig.filters?.quickbooksVendorName?.trim()) nextErrors.automationConfig = 'Configura el vendedor QuickBooks de la cartera.';
            if (
                isCoffeeVendingWeeklyRevenue &&
                (
                    form.automationConfig.metricCategory !== 'commercial_sales' ||
                    form.automationConfig.aggregationScope !== 'team_total' ||
                    form.automationConfig.businessUnit !== 'coffee_vending' ||
                    form.automationConfig.businessUnits?.length !== 1 ||
                    form.automationConfig.businessUnits?.[0] !== 'coffee_vending'
                )
            ) {
                nextErrors.automationConfig = 'La métrica semanal debe usar únicamente Coffee Vending y el total del equipo.';
            }
            if (form.automationConfig.metricCategory !== 'financial_report' && form.automationConfig.aggregationScope !== 'team_total' && !isKeyAccountRetention && !isCoffeeVendingWeeklyRevenue && !form.automationConfig.vendorName?.trim()) nextErrors.automationConfig = 'Escribe el vendedor de QuickBooks.';
        }
        if (form.automationConfig?.sourceType === 'parlevel') {
            if (!['route_sales', 'route_performance', 'operations', 'route_operations'].includes(form.automationConfig.metricCategory)) nextErrors.automationConfig = 'Selecciona el tipo de métrica de Parlevel.';
            if (!['routeSalesAmount', 'routeSalesDropCount', 'recurringMachineFailureCount', 'routeCompliancePercentage', 'routeComplianceAndParLevel'].includes(form.automationConfig.metricKey)) nextErrors.automationConfig = 'Selecciona una métrica de Parlevel válida.';
            if (form.automationConfig.metricKey === 'routeCompliancePercentage') {
                const targetValue = Number(form.automationConfig.thresholdConfig?.targetValue || form.weeklyTarget);
                const partialMinValue = Number(form.automationConfig.thresholdConfig?.partialMinValue);
                if (!targetValue || !partialMinValue || partialMinValue >= targetValue) nextErrors.automationConfig = 'Configura una meta completa mayor que el mínimo parcial.';
            }
            if (form.automationConfig.metricKey === 'routeComplianceAndParLevel') {
                const components = form.automationConfig.compositeConfig?.components || [];
                if (components.length < 2) nextErrors.automationConfig = 'Configura los componentes de cumplimiento y nivel par.';
            }
            if (form.automationConfig.metricKey === 'routeSalesAmount' && form.automationConfig.thresholdMetricKey) {
                if (form.automationConfig.thresholdMetricKey !== 'routeCompliancePercentage') nextErrors.automationConfig = 'Selecciona una condición válida de Parlevel.';
                if (form.automationConfig.thresholdValue === null || form.automationConfig.thresholdValue === undefined || form.automationConfig.thresholdValue === '') {
                    nextErrors.automationConfig = 'Indica el valor de la condición de cumplimiento de rutas.';
                }
            }
        }
        if (form.automationConfig?.sourceType === 'syncro_operations') {
            if (!['technical_operations', 'technical_quality', 'preventive_operations'].includes(form.automationConfig.metricCategory)) nextErrors.automationConfig = 'Selecciona el tipo de métrica de Syncro Operaciones.';
            if (!['machinesReadyForInstallationCount', 'openTechnicalCaseCount', 'averageRepairTimeHours', 'preventiveCompletionCount', 'preventiveCompletionPerRoute', 'ticketsPer10Machines'].includes(form.automationConfig.metricKey)) {
                nextErrors.automationConfig = 'Selecciona una métrica de Syncro Operaciones válida.';
            }
        }
        if (form.automationConfig?.sourceType === 'composite') {
            if (!['operations_quality', 'preventive_operations'].includes(form.automationConfig.metricCategory)) nextErrors.automationConfig = 'Selecciona el tipo de métrica compuesta.';
            if (!['margaritaFailurePreventionAttentionComposite', 'mauricioPreventiveOpenCasesComposite'].includes(form.automationConfig.metricKey)) nextErrors.automationConfig = 'Selecciona una métrica compuesta válida.';
            const components = form.automationConfig.compositeConfig?.components || [];
            const expectedComponents = form.automationConfig.metricKey === 'mauricioPreventiveOpenCasesComposite' ? 2 : 3;
            if (components.length !== expectedComponents) nextErrors.automationConfig = `La métrica compuesta debe tener ${expectedComponents} componentes.`;
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        onSubmit({
            ownerId: form.ownerId,
            pointOwnerType: 'user',
            pointOwnerUserId: form.ownerId,
            pointOwnerName: '',
            updateResponsibleUserId: form.ownerId,
            title: form.title.trim(),
            description: form.description || '',
            weight: Number(form.weight),
            weeklyTarget: form.calculationType === 'action_completed' ? 1 : form.weeklyTarget === null || form.weeklyTarget === undefined || form.weeklyTarget === '' ? null : Number(form.weeklyTarget),
            unit: form.calculationType === 'action_completed' ? 'acción' : form.unit || '',
            calculationType: getEffectiveCalculationType(form),
            scoringMethod: form.calculationType === 'action_completed' ? 'yes_no' : form.calculationType === 'max_allowed' ? 'max_allowed' : form.scoringMethod,
            allowOverTarget: Boolean(form.allowOverTarget),
            maxMultiplier: form.maxMultiplier === null || form.maxMultiplier === undefined || form.maxMultiplier === '' ? null : Number(form.maxMultiplier),
            automationConfig: {
                sourceType: form.automationConfig?.sourceType || 'manual',
                metricCategory: form.automationConfig?.sourceType === 'quickbooks'
                    ? form.automationConfig.metricCategory || 'commercial'
                    : form.automationConfig?.sourceType === 'parlevel'
                        ? form.automationConfig.metricCategory || 'route_sales'
                    : form.automationConfig?.sourceType === 'syncro_operations'
                        ? form.automationConfig.metricKey === 'preventiveCompletionPerRoute'
                            ? 'preventive_operations'
                            : ['ticketsPer10Machines', 'technicianRecurringMachineRate'].includes(form.automationConfig.metricKey)
                                ? 'technical_quality'
                                : 'technical_operations'
                    : form.automationConfig?.sourceType === 'composite'
                        ? form.automationConfig.metricKey === 'mauricioPreventiveOpenCasesComposite' ? 'preventive_operations' : 'operations_quality'
                        : null,
                metricKey: form.automationConfig?.sourceType !== 'manual' ? form.automationConfig.metricKey : null,
                reportKey: form.automationConfig?.sourceType === 'quickbooks' && form.automationConfig.metricKey === 'coffeeVendingWeeklyRevenue'
                    ? 'coffee_vending_weekly_revenue'
                    : form.automationConfig?.sourceType === 'quickbooks' && form.automationConfig.metricCategory === 'financial_report'
                        ? form.automationConfig.metricKey === 'grossMarginPercentage' ? 'profit_and_loss' : form.automationConfig.metricKey === 'overdueAccountsReceivablePercentage' ? 'accounts_receivable_aging' : form.automationConfig.reportKey || 'accounts_payable_aging'
                        : null,
                aggregationScope: form.automationConfig?.sourceType === 'quickbooks'
                    ? form.automationConfig.aggregationScope || 'individual_vendor'
                    : form.automationConfig?.sourceType === 'parlevel'
                        ? ['routeCompliancePercentage', 'routeComplianceAndParLevel', 'routeSalesDropCount'].includes(form.automationConfig.metricKey) ? 'all_routes' : 'all_machines'
                        : form.automationConfig?.sourceType === 'syncro_operations'
                            ? ['technicianAverageResponseTimeHours', 'technicianRecurringMachineRate'].includes(form.automationConfig.metricKey)
                                ? 'individual_technician'
                                : 'technical_team'
                            : form.automationConfig?.sourceType === 'composite' ? 'all_machines' : 'individual_vendor',
                businessUnit: form.automationConfig?.sourceType === 'quickbooks' ? form.automationConfig.businessUnit : null,
                businessUnits: form.automationConfig?.sourceType === 'quickbooks' ? form.automationConfig.businessUnits || [form.automationConfig.businessUnit].filter(Boolean) : [],
                team: form.automationConfig?.sourceType === 'quickbooks' ? form.automationConfig.team : null,
                vendorScope: form.automationConfig?.sourceType === 'quickbooks'
                    ? form.automationConfig.aggregationScope === 'team_total' ? 'all_team' : 'individual_vendor'
                    : null,
                vendorId: form.automationConfig?.vendorId || null,
                vendorName: form.automationConfig?.sourceType === 'quickbooks' && form.automationConfig.aggregationScope !== 'team_total' ? form.automationConfig.vendorName || '' : null,
                syncroUserId: form.ownerId,
                dateRangeMode: form.automationConfig?.sourceType === 'syncro_operations' && form.automationConfig.metricKey === 'openTechnicalCaseCount'
                    ? 'okr_week_end'
                    : form.automationConfig?.metricCategory === 'financial_report'
                        ? form.automationConfig.metricKey === 'grossMarginPercentage' ? 'okr_week' : 'okr_week_end'
                        : form.automationConfig?.dateRangeMode || 'okr_week',
                division: form.automationConfig?.division || null,
                classId: form.automationConfig?.classId || null,
                locationId: form.automationConfig?.locationId || null,
                thresholdMetricKey: form.automationConfig?.thresholdMetricKey || null,
                thresholdOperator: form.automationConfig?.thresholdMetricKey ? form.automationConfig.thresholdOperator || '>=' : null,
                thresholdValue:
                    form.automationConfig?.thresholdMetricKey &&
                    form.automationConfig.thresholdValue !== null &&
                    form.automationConfig.thresholdValue !== undefined &&
                    form.automationConfig.thresholdValue !== ''
                        ? Number(form.automationConfig.thresholdValue)
                        : null,
                thresholdConfig: form.automationConfig?.sourceType === 'parlevel' && form.automationConfig.metricKey === 'routeCompliancePercentage'
                    ? {
                        targetValue: Number(form.automationConfig.thresholdConfig?.targetValue || form.weeklyTarget || 98),
                        partialMinValue: Number(form.automationConfig.thresholdConfig?.partialMinValue || 95),
                        direction: form.automationConfig.thresholdConfig?.direction || 'higher_is_better'
                    }
                    : undefined,
                thresholds: form.automationConfig?.sourceType === 'syncro_operations' && form.automationConfig.metricKey === 'ticketsPer10Machines'
                    ? {
                        fullPointsAtOrBelow: Number(form.automationConfig.thresholds?.fullPointsAtOrBelow || 1),
                        halfPointsAt: Number(form.automationConfig.thresholds?.halfPointsAt || 2),
                        zeroPointsAtOrAbove: Number(form.automationConfig.thresholds?.zeroPointsAtOrAbove || 3)
                    }
                    : undefined,
                compositeConfig: form.automationConfig?.sourceType === 'quickbooks' && form.automationConfig.metricKey === 'grossMarginPercentage'
                    ? form.automationConfig.compositeConfig
                    : form.automationConfig?.sourceType === 'parlevel' && form.automationConfig.metricKey === 'routeComplianceAndParLevel'
                    ? form.automationConfig.compositeConfig || DEFAULT_COMPOSITE_CONFIG
                    : form.automationConfig?.sourceType === 'composite'
                        ? form.automationConfig.compositeConfig || getCompositeConfigForMetric(form.automationConfig.metricKey)
                    : undefined,
                filters: form.automationConfig?.sourceType === 'quickbooks' && form.automationConfig.metricKey === 'coffeeVendingWeeklyRevenue'
                    ? {
                        accountingMethod: 'Accrual',
                        machineSalesSource: 'parlevel_route_sales',
                        coffeeAccountId: '167',
                        contractsAccountId: '1150040007'
                    }
                    : ['parlevel', 'syncro_operations', 'composite'].includes(form.automationConfig?.sourceType)
                        ? form.automationConfig.metricKey === 'preventiveCompletionPerRoute'
                        ? {
                            ...(form.automationConfig.filters || {}),
                            inRoutesOnly: true,
                            expectedPerRoute: form.automationConfig.filters?.expectedPerRoute || 3
                        }
                        : form.automationConfig.metricKey === 'ticketsPer10Machines'
                            ? {
                                ownerUserName: form.automationConfig.filters?.ownerUserName || 'Julio Ortiz',
                                scope: 'assigned_machines'
                            }
                            : form.automationConfig.filters || {}
                        : undefined,
                isEnabled: form.automationConfig?.sourceType !== 'manual'
            }
        });
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={saving} />
            <Button label={isEditMode ? 'Guardar cambios' : 'Agregar resultado'} icon="pi pi-check" onClick={handleSubmit} loading={saving} />
        </div>
    );

    return (
        <Dialog header={title} visible={visible} onHide={onHide} footer={footer} className="w-full md:w-42rem" modal draggable={false}>
            <div className="surface-50 border-round p-3 mb-3">
                <div className="text-sm text-color-secondary">Equipo</div>
                <div className="font-semibold mb-2">{team?.name || '-'}</div>
                <div className="text-sm text-color-secondary">Objetivo principal</div>
                <div className="font-semibold">{objective?.title || '-'}</div>
                <small className="text-color-secondary block mt-2">
                    Define cómo se medirá el avance. Aquí se asignan los puntos, la meta y el tipo de cálculo.
                </small>
                {errors.objectiveId ? <small className="p-error">{errors.objectiveId}</small> : null}
            </div>

            <div className="formgrid grid">
                <div className="field col-12 md:col-6">
                    <label htmlFor="okr-kr-owner" className="font-medium">Persona responsable</label>
                    <Dropdown
                        id="okr-kr-owner"
                        value={form.ownerId}
                        options={ownerUserOptions}
                        onChange={(event) => updateField('ownerId', event.value)}
                        filter
                        className={`w-full mt-2 ${errors.ownerId ? 'p-invalid' : ''}`}
                        placeholder="Selecciona persona responsable"
                        disabled={hasSingleInferredOwner}
                    />
                    {hasSingleInferredOwner ? (
                        <small className="text-color-secondary block mt-1">Persona responsable definida por el objetivo principal.</small>
                    ) : null}
                    {hasMultipleObjectiveResponsibles ? (
                        <small className="text-color-secondary block mt-1">
                            Este objetivo tiene varios responsables. Selecciona quién será responsable de este resultado clave.
                        </small>
                    ) : (
                        <small className="text-color-secondary block mt-1">
                            Persona responsable de actualizar este resultado clave y recibir sus puntos.
                        </small>
                    )}
                    {errors.ownerId ? <small className="p-error">{errors.ownerId}</small> : null}
                </div>

                <div className="field col-12">
                    <label htmlFor="okr-kr-title" className="font-medium">Título del resultado clave medible</label>
                    <InputText
                        id="okr-kr-title"
                        value={form.title}
                        onChange={(event) => updateField('title', event.target.value)}
                        className={`w-full mt-2 ${errors.title ? 'p-invalid' : ''}`}
                    />
                    {errors.title ? <small className="p-error">{errors.title}</small> : null}
                </div>
            </div>

            <div className="surface-50 border-round p-3 mb-3">
                <div className="font-semibold mb-1">Puntaje OKR</div>
                <small className="text-color-secondary block mb-3">
                    Este peso define cuántos puntos aporta este resultado clave durante el ciclo.
                </small>

                <div className="formgrid grid">
                    <div className="field col-12 md:col-6">
                        <label htmlFor="okr-kr-weight" className="font-medium">Peso trimestral (puntos)</label>
                        <InputNumber
                            id="okr-kr-weight"
                            value={form.weight}
                            onValueChange={(event) => updateField('weight', event.value)}
                            min={1}
                            useGrouping
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            className={`w-full mt-2 ${errors.weight ? 'p-invalid' : ''}`}
                        />
                        {errors.weight ? <small className="p-error">{errors.weight}</small> : null}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Puntos por semana</label>
                        <div className="mt-2 p-3 border-1 surface-border border-round bg-white font-semibold">
                            Puntos por semana: {weeklyBasePoints}
                        </div>
                        <small className="text-color-secondary">Calculado con {numberOfWeeks || '-'} semanas del ciclo.</small>
                    </div>
                </div>
            </div>

            <div className="surface-50 border-round p-3 mb-3">
                <div className="font-semibold mb-3">Indicador de negocio</div>

                <div className="formgrid grid">
                    <div className="field col-12">
                        <label htmlFor="okr-kr-calculation-type" className="font-medium">Tipo de captura</label>
                        <Dropdown
                            id="okr-kr-calculation-type"
                            value={form.calculationType}
                            options={CALCULATION_TYPE_OPTIONS}
                            onChange={(event) => updateField('calculationType', event.value)}
                            className="w-full mt-2"
                        />
                        {form.calculationType === 'variance_forecast_vs_real' ? (
                            <small className="text-color-secondary block mt-1">
                                En la actualización, el usuario ingresará Forecast y Real. El sistema calculará automáticamente la varianza %.
                            </small>
                        ) : null}
                        {form.calculationType === 'max_allowed' ? (
                            <small className="text-color-secondary block mt-1">
                                El usuario reportará una cantidad. Si la cantidad es menor o igual a la meta, gana los puntos de la semana. Si la supera, gana 0.
                            </small>
                        ) : null}
                    </div>

                    {form.calculationType !== 'action_completed' ? (
                    <div className="field col-12 md:col-6">
                        <label htmlFor="okr-kr-target" className="font-medium">Meta esperada del indicador</label>
                        <InputNumber
                            id="okr-kr-target"
                            value={form.weeklyTarget}
                            onValueChange={(event) => updateField('weeklyTarget', event.value)}
                            min={0}
                            useGrouping
                            mode={form.unit === 'USD' ? 'currency' : 'decimal'}
                            currency={form.unit === 'USD' ? 'USD' : undefined}
                            locale="en-US"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            className={`w-full mt-2 ${errors.weeklyTarget ? 'p-invalid' : ''}`}
                        />
                        <small className="text-color-secondary block mt-1">
                            Este es el valor real que se medirá, no los puntos. Ejemplo: 23000 USD, 5 reclamos, 100%.
                        </small>
                        {errors.weeklyTarget ? <small className="p-error">{errors.weeklyTarget}</small> : null}
                    </div>
                    ) : (
                    <div className="field col-12 md:col-6">
                        <label className="font-medium">Meta esperada del indicador</label>
                        <div className="mt-2 p-3 border-1 surface-border border-round bg-white font-semibold">Acción completada: Sí / No</div>
                        <small className="text-color-secondary block mt-1">
                            El usuario marcará si la acción fue completada. Si se completó, gana los puntos correspondientes; si no, gana 0.
                        </small>
                    </div>
                    )}

                    <div className="field col-12 md:col-6">
                        <label htmlFor="okr-kr-unit" className="font-medium">Unidad del indicador</label>
                        <Dropdown
                            id="okr-kr-unit"
                            value={form.unit}
                            options={UNIT_OPTIONS}
                            onChange={(event) => updateField('unit', event.value)}
                            editable
                            showClear
                            className="w-full mt-2"
                            placeholder="Unidad"
                            disabled={form.calculationType === 'action_completed'}
                        />
                        <small className="text-color-secondary block mt-1">
                            No uses puntos aquí. Los puntos se calculan automáticamente desde el peso.
                        </small>
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="okr-kr-scoring" className="font-medium">Método de cálculo</label>
                        <Dropdown
                            id="okr-kr-scoring"
                            value={form.scoringMethod}
                            options={SCORING_METHOD_OPTIONS}
                            onChange={(event) => updateField('scoringMethod', event.value)}
                            className="w-full mt-2"
                            disabled={form.calculationType === 'action_completed' || form.calculationType === 'variance_forecast_vs_real' || form.calculationType === 'max_allowed'}
                        />
                        {calculationPreview ? <small className="text-color-secondary block mt-1">{calculationPreview}</small> : null}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="okr-kr-max-multiplier" className="font-medium">Multiplicador máximo opcional</label>
                        <InputNumber
                            id="okr-kr-max-multiplier"
                            value={form.maxMultiplier}
                            onValueChange={(event) => updateField('maxMultiplier', event.value)}
                            min={0}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            useGrouping
                            className={`w-full mt-2 ${errors.maxMultiplier ? 'p-invalid' : ''}`}
                        />
                        {errors.maxMultiplier ? <small className="p-error">{errors.maxMultiplier}</small> : null}
                    </div>

                    <div className="field col-12">
                        <div className="flex align-items-center gap-2">
                            <Checkbox
                                inputId="okr-kr-over-target"
                                checked={form.allowOverTarget}
                                onChange={(event) => updateField('allowOverTarget', event.checked)}
                            />
                            <label htmlFor="okr-kr-over-target">Permitir exceder meta</label>
                        </div>
                    </div>
                </div>
            </div>

            <div className="surface-50 border-round p-3">
                <div className="font-semibold mb-3">Medición y criterio</div>

                <div className="field mb-0">
                    <label htmlFor="okr-kr-description" className="font-medium">Criterio de medición</label>
                    <InputTextarea
                        id="okr-kr-description"
                        value={form.description}
                        onChange={(event) => updateField('description', event.target.value)}
                        rows={3}
                        className="w-full mt-2"
                        placeholder="Análisis de varianza forecast vs. real a nivel de Contribución Bruta. Meta: ≤5%."
                    />
                    <small className="text-color-secondary block mt-1">
                        Describe qué se mide y bajo qué criterio. Ejemplo: Análisis de varianza forecast vs. real a nivel de Contribución Bruta. Meta: ≤5%.
                    </small>
                </div>
            </div>

            <div className="surface-50 border-round p-3 mt-3">
                <div className="font-semibold mb-1">Fuente de datos</div>
                <small className="text-color-secondary block mb-3">
                    Opcional. Syncro puede traer el valor real de la semana OKR desde QuickBooks o Parlevel sin cambiar el cálculo de puntos.
                </small>

                <div className="formgrid grid">
                    <div className="field col-12 md:col-4">
                        <label htmlFor="okr-kr-source-type" className="font-medium">Fuente</label>
                        <Dropdown
                            id="okr-kr-source-type"
                            value={form.automationConfig?.sourceType || 'manual'}
                            options={DATA_SOURCE_OPTIONS}
                            optionDisabled="disabled"
                            onChange={(event) => updateAutomationField('sourceType', event.value)}
                            className="w-full mt-2"
                        />
                    </div>

                    {form.automationConfig?.sourceType === 'quickbooks' ? (
                        <>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="okr-kr-qb-metric-category" className="font-medium">Tipo de métrica</label>
                                <Dropdown
                                    id="okr-kr-qb-metric-category"
                                    value={form.automationConfig.metricCategory}
                                    options={QUICKBOOKS_METRIC_CATEGORY_OPTIONS}
                                    onChange={(event) => updateAutomationField('metricCategory', event.value)}
                                    className="w-full mt-2"
                                />
                            </div>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="okr-kr-qb-scope" className="font-medium">Alcance</label>
                                <Dropdown
                                    id="okr-kr-qb-scope"
                                    value={form.automationConfig.aggregationScope}
                                    options={QUICKBOOKS_SCOPE_OPTIONS}
                                    onChange={(event) => updateAutomationField('aggregationScope', event.value)}
                                    className="w-full mt-2"
                                    disabled={form.automationConfig.metricCategory === 'financial_report' || form.automationConfig.metricKey === 'coffeeVendingWeeklyRevenue'}
                                />
                            </div>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="okr-kr-qb-business-units" className="font-medium">Unidades de negocio</label>
                                <MultiSelect
                                    id="okr-kr-qb-business-units"
                                    value={form.automationConfig.businessUnits}
                                    options={QUICKBOOKS_BUSINESS_UNIT_OPTIONS}
                                    onChange={(event) => updateAutomationField('businessUnits', event.value)}
                                    className="w-full mt-2"
                                    display="chip"
                                    disabled={form.automationConfig.metricKey === 'coffeeVendingWeeklyRevenue'}
                                />
                                <small className="text-color-secondary block mt-1">
                                    {form.automationConfig.metricKey === 'coffeeVendingWeeklyRevenue'
                                        ? 'Máquinas desde Parlevel; café y contratos desde la compañía Coffee & Vending de QuickBooks.'
                                        : form.automationConfig.metricCategory === 'financial_report'
                                        ? 'El reporte financiero se consulta por compañía/unidad QuickBooks y se suma si seleccionas varias.'
                                        : 'Puedes sumar Empaque + Foods para metas comerciales por vendedor o equipo.'}
                                </small>
                            </div>
                            {form.automationConfig.metricCategory === 'financial_report' ? (
                                <div className="field col-12 md:col-4">
                                    <label className="font-medium">Reporte QuickBooks</label>
                                    <InputText value={form.automationConfig.metricKey === 'grossMarginPercentage' ? 'Profit and Loss' : form.automationConfig.metricKey === 'overdueAccountsReceivablePercentage' ? 'Accounts Receivable Aging' : 'Accounts Payable Aging'} className="w-full mt-2" readOnly />
                                    <small className="text-color-secondary block mt-1">
                                        {form.automationConfig.metricKey === 'grossMarginPercentage' ? 'Usa los ingresos y el beneficio bruto de la semana OKR en Devengado.' : 'Usa la fecha de cierre de la semana OKR como fecha de corte.'}
                                    </small>
                                </div>
                            ) : (
                                <div className="field col-12 md:col-4">
                                    <label htmlFor="okr-kr-qb-team" className="font-medium">Equipo QuickBooks</label>
                                    <Dropdown
                                        id="okr-kr-qb-team"
                                        value={form.automationConfig.team}
                                        options={QUICKBOOKS_TEAM_OPTIONS}
                                        onChange={(event) => updateAutomationField('team', event.value)}
                                        className="w-full mt-2"
                                        disabled={form.automationConfig.metricKey === 'coffeeVendingWeeklyRevenue'}
                                    />
                                </div>
                            )}
                            {form.automationConfig.metricCategory !== 'financial_report' && form.automationConfig.aggregationScope === 'team_total' ? (
                                <div className="field col-12 md:col-6">
                                    <label className="font-medium">Vendedor</label>
                                    <InputText value="Todo el equipo" className="w-full mt-2" readOnly />
                                    <small className="text-color-secondary block mt-1">
                                        Suma la métrica de todo el equipo para las unidades seleccionadas.
                                    </small>
                                </div>
                            ) : form.automationConfig.metricCategory !== 'financial_report' || form.automationConfig.metricKey === 'overdueAccountsReceivablePercentage' ? (
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="okr-kr-qb-vendor" className="font-medium">Vendedor QuickBooks</label>
                                    <InputText
                                        id="okr-kr-qb-vendor"
                                        value={form.automationConfig.vendorName}
                                        onChange={(event) => updateAutomationField('vendorName', event.target.value)}
                                        className="w-full mt-2"
                                        placeholder="Ejemplo: Juan Ameijeiras"
                                    />
                                    <small className="text-color-secondary block mt-1">Usa el nombre exactamente como aparece en QuickBooks. Para CxC, este vendedor define las facturas abiertas incluidas.</small>
                                </div>
                            ) : null}
                            <div className="field col-12 md:col-6">
                                <label htmlFor="okr-kr-qb-metric" className="font-medium">Métrica</label>
                                <Dropdown
                                    id="okr-kr-qb-metric"
                                    value={form.automationConfig.metricKey}
                                    options={form.automationConfig.metricCategory === 'financial_report'
                                        ? QUICKBOOKS_FINANCIAL_METRIC_OPTIONS
                                        : form.automationConfig.metricCategory === 'customer_acquisition'
                                            ? QUICKBOOKS_CUSTOMER_ACQUISITION_METRIC_OPTIONS
                                            : form.automationConfig.metricCategory === 'customer_diversification'
                                                ? QUICKBOOKS_CUSTOMER_DIVERSIFICATION_METRIC_OPTIONS
                                            : QUICKBOOKS_METRIC_OPTIONS}
                                    optionDisabled="disabled"
                                    onChange={(event) => updateAutomationField('metricKey', event.value)}
                                    className="w-full mt-2"
                                />
                                {form.automationConfig.metricCategory === 'financial_report' ? (
                                    <small className="text-color-secondary block mt-1">
                                        {form.automationConfig.metricKey === 'grossMarginPercentage'
                                            ? 'Calcula beneficio bruto / ingresos × 100. Evalúa por separado la meta de cada unidad de negocio.'
                                            : form.automationConfig.metricKey === 'overdueAccountsReceivablePercentage'
                                            ? 'Calcula CxC vencida / CxC total * 100 por vendedor. No usa Cobrado, Facturas ni ventas.'
                                            : 'Calcula CxP vencida / CxP total * 100. No usa CxC, cobrado ni ventas.'}
                                    </small>
                                ) : form.automationConfig.metricKey === 'coffeeVendingWeeklyRevenue' ? (
                                    <small className="text-color-secondary block mt-1">
                                        Máquinas: ventas semanales de Parlevel. Café: cuenta 412 de QuickBooks solo en la semana de cierre mensual. Contratos: cuenta 42 semanal en Devengado.
                                    </small>
                                ) : form.automationConfig.metricKey === 'keyCustomerRepurchaseDropCount' ? (
                                    <small className="text-color-secondary block mt-1">
                                        Cuenta clientes con caída de recompra de 20% o más contra el promedio mensual de los últimos 3 meses del dashboard QuickBooks Comercial.
                                    </small>
                                ) : form.automationConfig.metricKey === 'keyAccountRetentionRate' ? (
                                    <small className="text-color-secondary block mt-1">
                                        Calcula cuentas retenidas / cuentas bajo Administracion. Internamente usa el vendedor QuickBooks Administrativo y muestra la caída ≥20% solo como alerta.
                                    </small>
                                ) : form.automationConfig.metricKey === 'newCustomerAccountQualification' ? (
                                    <small className="text-color-secondary block mt-1">
                                        Otorga todos los puntos de la semana cuando QuickBooks detecta al menos el número configurado de clientes nuevos con venta comercial en Empaque o Foods.
                                    </small>
                                ) : form.automationConfig.metricKey === 'outsideTop10NetSales' ? (
                                    <small className="text-color-secondary block mt-1">
                                        Suma facturas menos notas de crédito fuera del Top 10 fijo de julio de 2026. Evalúa Empaques y Foods por separado, con 50% de los puntos para cada división.
                                    </small>
                                ) : null}
                            </div>
                            {form.automationConfig.metricCategory !== 'financial_report' ? (
                                <>
                                    {form.automationConfig.metricKey === 'keyCustomerRepurchaseDropCount' || form.automationConfig.metricKey === 'keyAccountRetentionRate' ? (
                                        <div className="field col-12 md:col-4">
                                            <label htmlFor="okr-kr-qb-repurchase-threshold" className="font-medium">Umbral de caída</label>
                                            <InputNumber
                                                id="okr-kr-qb-repurchase-threshold"
                                                value={form.automationConfig.metricKey === 'keyAccountRetentionRate'
                                                    ? form.automationConfig.filters?.atRiskDropThresholdPercentage ?? 20
                                                    : form.automationConfig.filters?.dropThresholdPercentage ?? 20}
                                                onValueChange={(event) => updateAutomationField('filters', {
                                                    ...(form.automationConfig.filters || {}),
                                                    ...(form.automationConfig.metricKey === 'keyAccountRetentionRate'
                                                        ? {
                                                            atRiskDropThresholdPercentage: event.value ?? 20,
                                                            baselineMode: 'previous_3_month_average'
                                                        }
                                                        : {
                                                            dropThresholdPercentage: event.value ?? 20,
                                                            comparisonBasis: 'trailing_3_month_average'
                                                        })
                                                })}
                                                suffix="%"
                                                min={0}
                                                maxFractionDigits={2}
                                                useGrouping={false}
                                                className="w-full mt-2"
                                            />
                                            <small className="text-color-secondary block mt-1">
                                                {form.automationConfig.metricKey === 'keyAccountRetentionRate'
                                                    ? 'Se muestra como alerta; no determina el puntaje principal de retención.'
                                                    : 'Para esta meta normalmente se usa 20% y meta máxima permitida 0 clientes.'}
                                            </small>
                                        </div>
                                    ) : null}
                                    {form.automationConfig.metricKey === 'newCustomerAccountQualification' ? (
                                        <div className="field col-12 md:col-4">
                                            <label htmlFor="okr-kr-qb-new-customer-count" className="font-medium">Clientes nuevos requeridos</label>
                                            <InputNumber
                                                id="okr-kr-qb-new-customer-count"
                                                value={form.automationConfig.filters?.requiredNewCustomerCount ?? 2}
                                                onValueChange={(event) => updateAutomationField('filters', {
                                                    ...(form.automationConfig.filters || {}),
                                                    requiredNewCustomerCount: Math.max(1, Number(event.value || 1))
                                                })}
                                                min={1}
                                                maxFractionDigits={0}
                                                useGrouping={false}
                                                className="w-full mt-2"
                                                disabled
                                            />
                                            <small className="text-color-secondary block mt-1">
                                                Con 2 o más clientes nuevos detectados, la semana recibe todos los puntos.
                                            </small>
                                        </div>
                                    ) : null}
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-qb-threshold-metric" className="font-medium">Condición opcional</label>
                                        <Dropdown
                                            id="okr-kr-qb-threshold-metric"
                                            value={form.automationConfig.thresholdMetricKey}
                                            options={[
                                                { label: 'Sin condición', value: '' },
                                                { label: 'Margen bruto % (no disponible por vendedor)', value: 'grossMarginPercentage' }
                                            ]}
                                            onChange={(event) => updateAutomationField('thresholdMetricKey', event.value)}
                                            className="w-full mt-2"
                                        />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-qb-threshold-operator" className="font-medium">Operador</label>
                                        <Dropdown
                                            id="okr-kr-qb-threshold-operator"
                                            value={form.automationConfig.thresholdOperator}
                                            options={THRESHOLD_OPERATOR_OPTIONS}
                                            onChange={(event) => updateAutomationField('thresholdOperator', event.value)}
                                            className="w-full mt-2"
                                            disabled={!form.automationConfig.thresholdMetricKey}
                                        />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-qb-threshold-value" className="font-medium">Valor condición</label>
                                        <InputNumber
                                            id="okr-kr-qb-threshold-value"
                                            value={form.automationConfig.thresholdValue}
                                            onValueChange={(event) => updateAutomationField('thresholdValue', event.value)}
                                            minFractionDigits={0}
                                            maxFractionDigits={2}
                                            useGrouping
                                            className="w-full mt-2"
                                            disabled={!form.automationConfig.thresholdMetricKey}
                                        />
                                    </div>
                                    {form.automationConfig.thresholdMetricKey === 'grossMarginPercentage' ? (
                                        <small className="col-12 text-orange-600">
                                            QuickBooks actualmente no expone margen bruto por vendedor en el servicio reutilizable. La sincronización avisará que este dato no está disponible.
                                        </small>
                                    ) : null}
                                </>
                            ) : null}
                            {errors.automationConfig ? <small className="col-12 p-error">{errors.automationConfig}</small> : null}
                        </>
                    ) : null}
                    {form.automationConfig?.sourceType === 'parlevel' ? (
                        <>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="okr-kr-parlevel-metric-category" className="font-medium">Tipo de métrica</label>
                                <Dropdown
                                    id="okr-kr-parlevel-metric-category"
                                    value={form.automationConfig.metricCategory}
                                    options={PARLEVEL_METRIC_CATEGORY_OPTIONS}
                                    onChange={(event) => updateAutomationField('metricCategory', event.value)}
                                    className="w-full mt-2"
                                />
                            </div>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="okr-kr-parlevel-metric" className="font-medium">Métrica</label>
                                <Dropdown
                                    id="okr-kr-parlevel-metric"
                                    value={form.automationConfig.metricKey}
                                    options={PARLEVEL_METRIC_OPTIONS[form.automationConfig.metricCategory] || PARLEVEL_METRIC_OPTIONS.route_sales}
                                    onChange={(event) => updateAutomationField('metricKey', event.value)}
                                    className="w-full mt-2"
                                />
                            </div>
                            <div className="field col-12 md:col-4">
                                <label className="font-medium">Alcance</label>
                                <InputText value="Todas las máquinas / rutas" className="w-full mt-2" readOnly />
                                <small className="text-color-secondary block mt-1">
                                    Usa el rango de fechas de la semana OKR.
                                </small>
                            </div>
                            {form.automationConfig.metricKey === 'recurringMachineFailureCount' ? (
                                <div className="field col-12 md:col-4">
                                    <label htmlFor="okr-kr-parlevel-failure-threshold" className="font-medium">Umbral de recurrencia</label>
                                    <InputNumber
                                        id="okr-kr-parlevel-failure-threshold"
                                        value={form.automationConfig.filters?.failureThreshold || 2}
                                        onValueChange={(event) => updateAutomationField('filters', { failureThreshold: event.value || 2 })}
                                        min={2}
                                        maxFractionDigits={0}
                                        useGrouping={false}
                                        className="w-full mt-2"
                                    />
                                    <small className="text-color-secondary block mt-1">
                                        Cuenta máquinas con 2 o más eventos de falla en la semana OKR. La meta permitida es 0 máquinas.
                                    </small>
                                </div>
                            ) : form.automationConfig.metricKey === 'routeSalesDropCount' ? (
                                <>
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-parlevel-drop-threshold" className="font-medium">Umbral de caída</label>
                                        <InputNumber
                                            id="okr-kr-parlevel-drop-threshold"
                                            value={form.automationConfig.filters?.dropThresholdPercentage ?? 15}
                                            onValueChange={(event) => updateAutomationField('filters', { dropThresholdPercentage: event.value ?? 15, comparison: 'previous_week' })}
                                            suffix="%"
                                            min={0}
                                            maxFractionDigits={2}
                                            useGrouping={false}
                                            className="w-full mt-2"
                                        />
                                        <small className="text-color-secondary block mt-1">
                                            Cuenta rutas cuya venta cae contra la semana previa. El tablero Parlevel usa de forma confiable caídas de 15% o más.
                                        </small>
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label className="font-medium">Comparación</label>
                                        <InputText value="Semana OKR vs semana previa" className="w-full mt-2" readOnly />
                                    </div>
                                </>
                            ) : form.automationConfig.metricKey === 'routeComplianceAndParLevel' ? (
                                <>
                                    <div className="field col-12 md:col-4">
                                        <label className="font-medium">Cumplimiento de ruta</label>
                                        <InputText value="Meta 98% · Parcial desde 95% · 50% del puntaje" className="w-full mt-2" readOnly />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label className="font-medium">Nivel par promedio</label>
                                        <InputText value="Meta 85% · 50% del puntaje" className="w-full mt-2" readOnly />
                                    </div>
                                    <div className="field col-12">
                                        <small className="text-color-secondary">
                                            Sincroniza Cumplimiento y Avg. Par Level desde Parlevel. El puntaje semanal se divide 50% cumplimiento de ruta y 50% nivel par.
                                        </small>
                                    </div>
                                </>
                            ) : form.automationConfig.metricKey === 'routeCompliancePercentage' ? (
                                <>
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-parlevel-compliance-target" className="font-medium">Meta completa</label>
                                        <InputNumber
                                            id="okr-kr-parlevel-compliance-target"
                                            value={form.automationConfig.thresholdConfig?.targetValue || 98}
                                            onValueChange={(event) => {
                                                updateAutomationField('thresholdConfig', { targetValue: event.value || 98 });
                                                updateField('weeklyTarget', event.value || 98);
                                            }}
                                            suffix="%"
                                            minFractionDigits={0}
                                            maxFractionDigits={2}
                                            useGrouping
                                            className="w-full mt-2"
                                        />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-parlevel-compliance-partial" className="font-medium">Mínimo para puntos parciales</label>
                                        <InputNumber
                                            id="okr-kr-parlevel-compliance-partial"
                                            value={form.automationConfig.thresholdConfig?.partialMinValue || 95}
                                            onValueChange={(event) => updateAutomationField('thresholdConfig', { partialMinValue: event.value || 95 })}
                                            suffix="%"
                                            minFractionDigits={0}
                                            maxFractionDigits={2}
                                            useGrouping
                                            className="w-full mt-2"
                                        />
                                    </div>
                                    <div className="field col-12">
                                        <small className="text-color-secondary">
                                            Usa Cumplimiento de rutas de Parlevel como valor real. Si llega a la meta completa gana todos los puntos semanales; entre el mínimo parcial y la meta gana puntos proporcionales.
                                        </small>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-parlevel-threshold-metric" className="font-medium">Condición opcional</label>
                                        <Dropdown
                                            id="okr-kr-parlevel-threshold-metric"
                                            value={form.automationConfig.thresholdMetricKey}
                                            options={[
                                                { label: 'Sin condición', value: '' },
                                                { label: 'Cumplimiento de rutas %', value: 'routeCompliancePercentage' }
                                            ]}
                                            onChange={(event) => updateAutomationField('thresholdMetricKey', event.value)}
                                            className="w-full mt-2"
                                        />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-parlevel-threshold-operator" className="font-medium">Operador</label>
                                        <Dropdown
                                            id="okr-kr-parlevel-threshold-operator"
                                            value={form.automationConfig.thresholdOperator}
                                            options={THRESHOLD_OPERATOR_OPTIONS}
                                            onChange={(event) => updateAutomationField('thresholdOperator', event.value)}
                                            className="w-full mt-2"
                                            disabled={!form.automationConfig.thresholdMetricKey}
                                        />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="okr-kr-parlevel-threshold-value" className="font-medium">Valor condición</label>
                                        <InputNumber
                                            id="okr-kr-parlevel-threshold-value"
                                            value={form.automationConfig.thresholdValue}
                                            onValueChange={(event) => updateAutomationField('thresholdValue', event.value)}
                                            suffix="%"
                                            minFractionDigits={0}
                                            maxFractionDigits={2}
                                            useGrouping
                                            className="w-full mt-2"
                                            disabled={!form.automationConfig.thresholdMetricKey}
                                        />
                                    </div>
                                    <div className="field col-12">
                                        <small className="text-color-secondary">
                                            Ventas en rutas suma la venta total Parlevel disponible para todas las máquinas/rutas. Si agregas cumplimiento de rutas, la venta solo cuenta para puntos cuando cumple la condición.
                                        </small>
                                    </div>
                                </>
                            )}
                            {errors.automationConfig ? <small className="col-12 p-error">{errors.automationConfig}</small> : null}
                        </>
                    ) : null}
                    {form.automationConfig?.sourceType === 'syncro_operations' ? (
                        <>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="okr-kr-syncro-ops-metric" className="font-medium">Métrica</label>
                                <Dropdown
                                    id="okr-kr-syncro-ops-metric"
                                    value={form.automationConfig.metricKey}
                                    options={SYNCRO_OPERATIONS_METRIC_OPTIONS}
                                    onChange={(event) => updateAutomationField('metricKey', event.value)}
                                    className="w-full mt-2"
                                />
                            </div>
                            <div className="field col-12 md:col-4">
                                <label className="font-medium">Alcance</label>
                                <InputText value={form.automationConfig.metricKey === 'machinesInStockForInstallationCount' ? 'Café y Snacks en inventario' : ['technicianAverageResponseTimeHours', 'technicianRecurringMachineRate'].includes(form.automationConfig.metricKey) ? 'Técnico responsable del OKR' : form.automationConfig.metricKey === 'ticketsPer10Machines' ? 'Máquinas asignadas a Julio Ortiz' : 'Equipo técnico'} className="w-full mt-2" readOnly />
                                <small className="text-color-secondary block mt-1">
                                    {form.automationConfig.metricKey === 'machinesInStockForInstallationCount'
                                        ? 'Listado de máquinas: Café y Snacks con estado En inventario; excluye registros eliminados.'
                                        : ['technicianAverageResponseTimeHours', 'technicianRecurringMachineRate'].includes(form.automationConfig.metricKey)
                                        ? 'Usa exclusivamente los tickets atribuidos al técnico responsable de este resultado clave.'
                                        : form.automationConfig.metricKey === 'ticketsPer10Machines'
                                        ? 'Usa máquinas asignadas a Julio Ortiz y tickets creados sobre esas máquinas.'
                                        : 'Usa datos operativos registrados en Syncro para la semana OKR.'}
                                </small>
                            </div>
                            <div className="field col-12 md:col-4">
                                <label className="font-medium">Rango</label>
                                <InputText
                                    value={form.automationConfig.metricKey === 'machinesInStockForInstallationCount' ? 'Inventario actual' : form.automationConfig.metricKey === 'openTechnicalCaseCount' ? 'Corte de semana OKR' : 'Semana OKR'}
                                    className="w-full mt-2"
                                    readOnly
                                />
                                <small className="text-color-secondary block mt-1">
                                    {form.automationConfig.metricKey === 'machinesInStockForInstallationCount'
                                        ? 'Cuenta máquinas En inventario al consultar. No reconstruye inventarios históricos ni cuenta instalaciones realizadas.'
                                        : form.automationConfig.metricKey === 'machinesReadyForInstallationCount'
                                        ? 'Cuenta movimientos instalados o intercambios instalados cerrados en la semana.'
                                        : form.automationConfig.metricKey === 'openTechnicalCaseCount'
                                            ? 'Cuenta órdenes técnicas abiertas al cierre de la semana, no casos creados.'
                                            : form.automationConfig.metricKey === 'technicianAverageResponseTimeHours'
                                                ? 'Mide desde la creación hasta la primera toma de responsabilidad; los tickets pendientes acumulan tiempo hasta el cierre semanal.'
                                            : form.automationConfig.metricKey === 'technicianRecurringMachineRate'
                                                ? 'Cuenta la misma máquina y categoría de falla con antecedente en los 30 días anteriores.'
                                            : form.automationConfig.metricKey === 'averageRepairTimeHours'
                                                ? 'Promedia horas de reparación de órdenes cerradas durante la semana.'
                                                : form.automationConfig.metricKey === 'ticketsPer10Machines'
                                                    ? 'Normaliza tickets creados por volumen: tickets / máquinas asignadas x 10.'
                                                    : 'Cuenta preventivos completados durante la semana OKR.'}
                                </small>
                            </div>
                            {errors.automationConfig ? <small className="col-12 p-error">{errors.automationConfig}</small> : null}
                        </>
                    ) : null}
                    {form.automationConfig?.sourceType === 'composite' ? (
                        <>
                            <div className="field col-12 md:col-4">
                                <label className="font-medium">Métrica compuesta</label>
                                <Dropdown
                                    value={form.automationConfig.metricKey}
                                    options={COMPOSITE_METRIC_OPTIONS}
                                    onChange={(event) => updateAutomationField('metricKey', event.value)}
                                    className="w-full mt-2"
                                />
                            </div>
                            {form.automationConfig.metricKey === 'mauricioPreventiveOpenCasesComposite' ? (
                                <>
                                    <div className="field col-12 md:col-4">
                                        <label className="font-medium">Componentes</label>
                                        <InputText value="2 componentes · 50% cada uno" className="w-full mt-2" readOnly />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label className="font-medium">Reglas</label>
                                        <InputText value="30 preventivos · menos de 75 casos abiertos" className="w-full mt-2" readOnly />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="field col-12 md:col-4">
                                        <label className="font-medium">Componentes</label>
                                        <InputText value="3 componentes · 33.33% cada uno" className="w-full mt-2" readOnly />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                        <label className="font-medium">Preventivos</label>
                                        <InputText value="Meta 27 · Fumigation + General Cleaning + combinado" className="w-full mt-2" readOnly />
                                    </div>
                                </>
                            )}
                            <div className="field col-12">
                                <small className="text-color-secondary">
                                    {form.automationConfig.metricKey === 'mauricioPreventiveOpenCasesComposite'
                                        ? 'Usa Mantenimiento Preventivo para preventivos completados y Syncro Operaciones para casos abiertos al cierre semanal. No usa QuickBooks.'
                                        : 'Usa Parlevel para coordinación y fallas recurrentes, y Syncro Operaciones para preventivos completados. No usa QuickBooks.'}
                                </small>
                            </div>
                            {errors.automationConfig ? <small className="col-12 p-error">{errors.automationConfig}</small> : null}
                        </>
                    ) : null}
                </div>
            </div>
        </Dialog>
    );
};

export default OkrKeyResultDialog;
